import { Outlet, useLocation } from 'react-router'
import styled from 'styled-components'

import { useAuthContext } from 'features/auth/auth-context'
import { NotificationsContainer } from 'features/notifications'

/**
 * Layout that wraps every authenticated route and mounts the global
 * notifications bell + panel in the top-right corner.
 *
 * The bell is rendered as a **fixed-position overlay** for most routes
 * (project pages, settings, profile, admin landing, trial, etc.) — the
 * overlay sits below ``ProjectLayout``'s top bar so it doesn't overlap
 * the existing Help / ReportBug buttons.
 *
 * On routes whose layout already has a natural button row (the projects
 * list at ``/`` and project pages) the bell is rendered INLINE inside that
 * layout's controls; the admin area shows no bell at all. To avoid two bells
 * stacking (or a bell over admin's page actions), we suppress this overlay
 * for those paths via ``OVERLAY_SUPPRESS_PATHS``.
 *
 * The bell is gated on ``isAuthenticated`` so unauthenticated routes
 * (sign-in, trial signup, password setup) don't render it.
 */
export const SignedInLayout = (): JSX.Element => {
  const { isAuthenticated } = useAuthContext()
  const { pathname } = useLocation()
  const overlaySuppressed = OVERLAY_SUPPRESS_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  )

  return (
    <>
      <Outlet />
      {isAuthenticated && !overlaySuppressed && (
        <BellSlot>
          <NotificationsContainer />
        </BellSlot>
      )}
    </>
  )
}

// Routes whose layout renders the bell inline, or (admin) shows none (so
// this layout's overlay must NOT render). Update this list whenever
// a layout adopts inline mounting.
const OVERLAY_SUPPRESS_PATHS = [
  '/', // HomeView (projects list) — bell in the Controls row
  '/admin', // AdminShell — no bell; it would sit over page actions
  '/project', // ProjectLayout's TopNavBar — bell to the LEFT of Report bug
]

/**
 * Fixed-position slot for the notifications bell on routes whose layout
 * doesn't host the bell inline. Sits BELOW existing layout top bars
 * (e.g. ``ProjectLayout``'s TopNavBar at ~48px tall) so the bell doesn't
 * overlap their right-side action clusters (Help / ReportBug etc.).
 * Above content (``z-index: 1100``) so it stays clickable as the page
 * scrolls; below modals (``z-index: 2000`` and up).
 */
const BellSlot = styled.div`
  position: fixed;
  top: 56px;
  right: 16px;
  z-index: 1100;
  display: flex;
  align-items: center;
`
